/*eslint-disable*/
import React from 'react';
import ReactModal from 'react-modal';
import moment from 'moment';
import closeSvg from 'src/assets/close.svg';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { IBenefitsConfigVersion } from 'src/interfaces';
import { CommonInput, DatePicker, RegularButton } from 'pg-ui-library';
import * as yup from 'yup';
import { MultiSelectDropdown } from './multi-select-dropdown';

import {
  CloseIcon,
  BenefitFormTitle,
  BenefitFormContent,
  FormTitle,
  BenefitVersionFormContainer,
  FormItem,
  SubmitContainer,
  StyledErrorWrap,
} from './styles';

import {
  useBenefitsConfigVersion,
  useBenefitsConfigVersionEditModalIsOpen,
  useSetBenefitsConfigVersionEditModal,
} from '../benefits-config-version';
import { useEditBenefitsConfigVersion } from './mutations/edit-benefits-config-version.mutation';
import { usePostBenefitsConfigVersion } from './mutations/post-benefits-config-version.mutation';
import { useDuplicateBenefitsConfigVersionWithData } from './mutations/duplicate-benefits-config-version-with-data.mutation';

export const BenefitsConfigVersionForm: React.FC<{ bVersion: IBenefitsConfigVersion }> = ({ bVersion }) => {
  const { t } = useTranslation();
  const setIsOpen = useSetBenefitsConfigVersionEditModal();
  const editMutation = useEditBenefitsConfigVersion();
  const postMutation = usePostBenefitsConfigVersion();
  const duplicateMutation = useDuplicateBenefitsConfigVersionWithData();

  const eventOptions = [
    { key: 'new-hire', label: 'New Hire', value: 'New Hire' },
    { key: 'open-enrollment', label: 'Open Enrollment', value: 'Open Enrollment' },
    { key: 'qle', label: 'QLE', value: 'QLE' },
    { key: 'benefit-update', label: 'Benefit Update', value: 'Benefit Update' },
  ];

  const getInitialEventValue = () => {
    if (Array.isArray(bVersion.event)) {
      return bVersion.event;
    }
    if (bVersion.event) {
      return [bVersion.event];
    }
    return [];
  };

  const isDuplicateMode = !bVersion.id;
  // Store the original ID for duplication
  const originalId = (bVersion as any).originalId || bVersion.id;

  const schema = yup.object({
    startDate: yup.date().max(yup.ref('endDate'), 'Start date must be earlier than end date').required(),
    endDate: yup.date().min(yup.ref('startDate'), 'End date must be later than start date').required(),
    payrollStartDate: yup.date().max(yup.ref('payrollEndDate'), 'Start date must be earlier than end date').required(),
    payrollEndDate: yup.date().min(yup.ref('payrollStartDate'), 'End date must be later than start date').required(),
    description: yup.string().max(255),
    event: yup.array().of(yup.string()),
  });

  const formik = useFormik<IBenefitsConfigVersion>({
    initialValues: {
      id: bVersion.id,
      startDate: bVersion.startDate ? new Date(bVersion.startDate) : new Date(),
      endDate: bVersion.endDate ? new Date(bVersion.endDate) : new Date(),
      payrollStartDate: bVersion.payrollStartDate ? new Date(bVersion.payrollStartDate) : new Date(),
      type: bVersion.type || [],
      payrollEndDate: bVersion.payrollEndDate ? new Date(bVersion.payrollEndDate) : new Date(),
      description: bVersion.description,
      version: bVersion.version,
      event: isDuplicateMode ? [] : getInitialEventValue(),
    },
    validationSchema: schema,
    onSubmit: values => {
      if (isDuplicateMode && originalId) {
        // Duplicate mode - use the new mutation with custom data
        const dataToSend = {
          ...values,
          originalId,
          startDate: moment(values.startDate).format('YYYY-MM-DD'),
          endDate: moment(values.endDate).format('YYYY-MM-DD'),
          payrollStartDate: moment(values.payrollStartDate).format('YYYY-MM-DD'),
          payrollEndDate: moment(values.payrollEndDate).format('YYYY-MM-DD'),
        };
        console.log('Sending data to duplicate:', dataToSend);
        duplicateMutation.mutateAsync(dataToSend).then(() => setIsOpen(false));
      } else if (values.id) {
        // Edit mode
        editMutation
          .mutateAsync({
            ...values,
            startDate: moment(values.startDate).format('YYYY-MM-DD'),
            endDate: moment(values.endDate).format('YYYY-MM-DD'),
            payrollStartDate: moment(values.payrollStartDate).format('YYYY-MM-DD'),
            payrollEndDate: moment(values.payrollEndDate).format('YYYY-MM-DD'),
          })
          .then(() => setIsOpen(false));
      } else {
        // Create new mode
        postMutation
          .mutateAsync({
            ...values,
            startDate: new Date(values.startDate).toISOString(),
            endDate: new Date(values.endDate).toISOString(),
            payrollStartDate: new Date(values.payrollStartDate).toISOString(),
            payrollEndDate: new Date(values.payrollEndDate).toISOString(),
          })
          .then(() => setIsOpen(false));
      }
    },
  });

  return (
    <BenefitVersionFormContainer>
      <FormItem style={{ flex: '0 0 calc(50% - 20px)' }}>
        <DatePicker
          id="startdate"
          name="startDate"
          label="Start Date*"
          selected={formik.values.startDate as Date}
          dateFormat="MM/dd/yyyy"
          onChange={(val: Date) => {
            formik.setFieldValue('startDate', val);
          }}
        />
        <StyledErrorWrap>{formik.errors.startDate}</StyledErrorWrap>
      </FormItem>
      <FormItem style={{ flex: '0 0 calc(50% - 20px)' }}>
        <DatePicker
          id="endDate"
          name="endDate"
          label="End Date*"
          dateFormat="MM/dd/yyyy"
          selected={formik.values.endDate as Date}
          onChange={(val: Date) => {
            formik.setFieldValue('endDate', val);
          }}
        />
        <StyledErrorWrap>{formik.errors.endDate}</StyledErrorWrap>
      </FormItem>

      <FormItem style={{ flex: '0 0 calc(50% - 20px)' }}>
        <DatePicker
          id="payrollStartDate"
          name="payrollStartDate"
          label="Payroll Start Date*"
          selected={formik.values.payrollStartDate as Date}
          dateFormat="MM/dd/yyyy"
          onChange={(val: Date) => {
            formik.setFieldValue('payrollStartDate', val);
          }}
        />
        <StyledErrorWrap>{formik.errors.payrollStartDate}</StyledErrorWrap>
      </FormItem>

      <FormItem style={{ flex: '0 0 calc(50% - 20px)' }}>
        <DatePicker
          id="payrollEndDate"
          name="payrollEndDate"
          label="Payroll End Date*"
          dateFormat="MM/dd/yyyy"
          selected={formik.values.payrollEndDate as Date}
          onChange={(val: Date) => {
            formik.setFieldValue('payrollEndDate', val);
          }}
        />
        <StyledErrorWrap>{formik.errors.payrollEndDate}</StyledErrorWrap>
      </FormItem>

      <FormItem style={{ flex: '0 0 calc(100% - 20px )' }}>
        <MultiSelectDropdown
          id="event"
          name="event"
          label="Event"
          options={eventOptions}
          selectedValues={Array.isArray(formik.values.event) ? formik.values.event : []}
          onChange={selectedValues => formik.setFieldValue('event', selectedValues)}
        />
        <StyledErrorWrap>{formik.errors.event}</StyledErrorWrap>
      </FormItem>

      <FormItem style={{ flex: '0 0 calc(100% - 20px )' }}>
        <CommonInput
          style={{ width: '100%' }}
          name="description"
          label="Description"
          value={formik.values.description}
          onChange={formik.handleChange}
        />
        <StyledErrorWrap>{formik.errors.description}</StyledErrorWrap>
      </FormItem>

      <SubmitContainer>
        <RegularButton
          style={{ margin: '0 3px 0 0' }}
          type="submit"
          onClick={() => formik.handleSubmit()}
          disabled={!formik.isValid || editMutation.isLoading || postMutation.isLoading || duplicateMutation.isLoading}
        >
          {editMutation.isLoading || postMutation.isLoading || duplicateMutation.isLoading
            ? 'Saving...'
            : t('global.form.save')}
        </RegularButton>
      </SubmitContainer>
    </BenefitVersionFormContainer>
  );
};

export const BenefitsConfigVersionModal: React.FC = () => {
  const { t } = useTranslation();
  const isOpen = useBenefitsConfigVersionEditModalIsOpen();
  const setIsOpen = useSetBenefitsConfigVersionEditModal();

  const selectedBenefitVersion = useBenefitsConfigVersion();

  const handleModal = () => {
    setIsOpen(!isOpen);
  };

  // Determine if this is a duplicate operation (no ID means it's a new version being created from an existing one)
  const isDuplicateMode = selectedBenefitVersion && !selectedBenefitVersion.id;
  const modalTitle = isDuplicateMode ? 'Duplicate Benefits Config Version' : t('benefits.configVersion.edit.title');

  return (
    <ReactModal
      isOpen={isOpen}
      ariaHideApp={false}
      onRequestClose={handleModal}
      style={{
        overlay: {
          margin: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
        },
        content: {
          borderRadius: '20px',
          margin: 'auto',
          width: '750px',
          height: '500px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f3f4f8',
        },
      }}
    >
      <BenefitFormTitle>
        <FormTitle>{modalTitle}</FormTitle>
        <div>
          <CloseIcon src={closeSvg} onClick={handleModal} />
        </div>
      </BenefitFormTitle>
      <BenefitFormContent>
        {selectedBenefitVersion && <BenefitsConfigVersionForm bVersion={selectedBenefitVersion} />}
      </BenefitFormContent>
    </ReactModal>
  );
};
